/**
 * Provenance of power values at a Hausanschluss — every figure in the house
 * sidebar says where it comes from (doc/Architektur/ERP_MELO_TAF_MEASUREMENT.md).
 *
 * `LoadInputSource` mirrors `MeasurementConstants.LoadInputSources` (backend):
 * what the measurement pool fed into the load flow for this house.
 */
export type LoadInputSource = 'measured' | 'outlet_balance' | 'load_profile' | 'annual_average';

const LOAD_INPUT_SOURCES: readonly LoadInputSource[] = ['measured', 'outlet_balance', 'load_profile', 'annual_average'];

/** Wire value → known source; unknown/missing → null (older pool, archive frame). */
export const parseLoadInputSource = (value: unknown): LoadInputSource | null =>
  typeof value === 'string' && (LOAD_INPUT_SOURCES as readonly string[]).includes(value)
    ? value as LoadInputSource
    : null;

/** Short badge text per source. */
export const LOAD_INPUT_SOURCE_LABELS: Record<LoadInputSource, string> = {
  measured: 'gemessen · TAF 10',
  outlet_balance: 'geschätzt · Abgangsmessung',
  load_profile: 'geschätzt · Lastprofil',
  annual_average: 'geschätzt · Jahresmittel',
};

/** Tooltip per source: how the value came about. */
export const LOAD_INPUT_SOURCE_DESCRIPTIONS: Record<LoadInputSource, string> = {
  measured: 'Gemessen: Live-Wert des Smart Meter Gateways (TAF 10, Netzzustandsdaten) an diesem Anschluss.',
  outlet_balance: 'Geschätzt: gemessener Abgang bzw. Trafo minus die gemessenen Anschlüsse, anteilig nach Jahresverbrauch verteilt — keine Messung an diesem Anschluss.',
  load_profile: 'Geschätzt: Lastprofil des Anschlusses (Standard- oder eigenes Profil) zur aktuellen Viertelstunde, skaliert mit dem Jahresverbrauch — keine Messung an diesem Anschluss.',
  annual_average: 'Geschätzt: Jahresverbrauch ÷ 8760 h ohne Tagesgang (kein Lastprofilwert verfügbar) — keine Messung an diesem Anschluss.',
};

/** Provenance of the connection's total power as one badge. */
export type ProvenanceKind = 'measured' | 'partial' | 'estimated' | 'unknown';

export interface Provenance {
  kind: ProvenanceKind;
  label: string;
  description: string;
}

/** Counts of the meter list (`buildMeterRows`) the house badge needs. */
interface MeterCounts {
  measuredCount: number;
  estimatedCount: number;
}

/**
 * Live provenance of a Hausanschluss value: what the load flow was fed with
 * (`inputSource` from the measurement pool), refined by the meter list — a
 * measured connection whose further meters are only estimated is "teilweise".
 */
export function houseProvenance(inputSource: LoadInputSource | null | undefined, meters: MeterCounts): Provenance {
  if (inputSource === 'measured' || (inputSource == null && meters.measuredCount > 0)) {
    if (meters.estimatedCount > 0) {
      const n = meters.measuredCount + meters.estimatedCount;
      return {
        kind: 'partial',
        label: 'teilweise gemessen',
        description: `${meters.measuredCount} von ${n} Zählern sind per TAF 10 gemessen. Der Lastfluss rechnet mit den gemessenen Zählern; die übrigen sind nur geschätzt (Zählerliste unten) und im Lastfluss nicht enthalten.`,
      };
    }
    return {
      kind: 'measured',
      label: LOAD_INPUT_SOURCE_LABELS.measured,
      description: inputSource === 'measured'
        ? LOAD_INPUT_SOURCE_DESCRIPTIONS.measured
        : 'Gemessen per TAF 10. Der Mess-Pool hat die Herkunft des Lastfluss-Eingangs nicht gemeldet (ältere Version).',
    };
  }
  if (inputSource) {
    return { kind: 'estimated', label: LOAD_INPUT_SOURCE_LABELS[inputSource], description: LOAD_INPUT_SOURCE_DESCRIPTIONS[inputSource] };
  }
  return {
    kind: 'unknown',
    label: 'Herkunft unbekannt',
    description: 'Der Mess-Pool hat nicht gemeldet, ob dieser Wert gemessen oder geschätzt ist (ältere Version oder noch kein Lastfluss).',
  };
}

/** Archived meter inputs of one connection at the rewound instant (`meterValues` of the snapshot). */
interface ArchivedMeterValue {
  simulated?: boolean;
  source?: string;
}

/**
 * Provenance of an archived frame value: the snapshot's meter inputs tell
 * which values were measured. `undefined` = the archive carries no inputs.
 */
export function archiveProvenance(values: readonly ArchivedMeterValue[] | undefined): Provenance {
  if (values === undefined) {
    return { kind: 'unknown', label: 'Archiv', description: 'Archivierter Lastflusswert. Dieser Archivstand enthält keine Eingangswerte, die Herkunft ist nicht bekannt.' };
  }
  const estimated = values.filter(v => v.simulated === true || v.source != null).length;
  const measured = values.length - estimated;
  if (measured === 0) {
    return { kind: 'estimated', label: 'geschätzt · Archiv', description: 'Archivierter Lastflusswert. Zu diesem Zeitpunkt lag für den Anschluss keine Messung vor, der Eingang war geschätzt.' };
  }
  if (estimated > 0) {
    return { kind: 'partial', label: 'teilweise gemessen · Archiv', description: `Archivierter Lastflusswert aus ${measured} gemessenen und ${estimated} geschätzten Zählerwerten.` };
  }
  return { kind: 'measured', label: 'gemessen · Archiv', description: 'Archivierter Lastflusswert; alle Zählerwerte zu diesem Zeitpunkt waren Messungen.' };
}

/** Tailwind classes of a provenance badge (light + dark). */
export const PROVENANCE_BADGE_CLASSES: Record<ProvenanceKind, string> = {
  measured: 'bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-300',
  partial: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
  estimated: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300',
  unknown: 'bg-gray-100 dark:bg-primary-700 text-gray-500 dark:text-gray-400',
};
